import React, { useCallback, useEffect, useState } from 'react';
import { listarFacturas, listarNotasCredito, anularFactura, anularDocumentoDirecto, listarFacturasLocales, reintentarFacturaLocal } from '../../../services/contificoService';
import { showToast } from '../../../utils/showToast';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '../../../styles/dashboardTheme.css';
import '../../../styles/managementTheme.css';

// ── Constantes ────────────────────────────────────────────────────────────────

const ESTADOS = {
  P: { label: 'Pendiente',   color: 'warning' },
  C: { label: 'Cobrado',     color: 'success' },
  G: { label: 'Pagado',      color: 'success' },
  A: { label: 'Anulado',     color: 'danger'  },
  E: { label: 'Generado',    color: 'info'    },
  F: { label: 'Facturado',   color: 'primary' },
};

// OJO: nunca usar new Date().toISOString().slice(0,10) para "hoy" — toISOString()
// convierte a UTC, y en Ecuador (UTC-5) desde las 19:00 ya marca el dia siguiente.
const hoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const primerDiaMes = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatFecha(f) {
  if (!f) return '—';
  // Contífico devuelve dd/mm/aaaa
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(f)) {
    const [d, m, y] = f.split('/');
    return `${d}/${m}/${y}`;
  }
  return f;
}

function formatMonto(v) {
  return Number(v || 0).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function BadgeEstado({ estado }) {
  const e = ESTADOS[estado] || { label: estado || '—', color: 'secondary' };
  return <span className={`badge bg-${e.color}`}>{e.label}</span>;
}

function BadgeSRI({ autorizado, enviado }) {
  if (autorizado) return <span className="badge bg-success"><i className="bi bi-check-circle-fill me-1" />Autorizado</span>;
  if (enviado)    return <span className="badge bg-warning text-dark"><i className="bi bi-hourglass-split me-1" />En proceso</span>;
  return <span className="badge bg-secondary"><i className="bi bi-dash me-1" />No enviado</span>;
}

// ── Panel de detalle ──────────────────────────────────────────────────────────

function ResumenFacturaBox({ factura }) {
  const persona = factura.cliente || {};
  return (
    <div className="bg-light rounded-3 p-3 mb-3">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <span className="text-muted" style={{ fontSize: 12 }}>N° DOCUMENTO</span>
        <span className="fw-bold">{factura.documento || factura.id}</span>
      </div>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <span className="text-muted" style={{ fontSize: 12 }}>CLIENTE</span>
        <span className="fw-semibold text-end" style={{ maxWidth: '60%' }}>{persona.razon_social || '—'}</span>
      </div>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <span className="text-muted" style={{ fontSize: 12 }}>IDENTIFICACIÓN</span>
        <span>{persona.cedula || persona.ruc || '—'}</span>
      </div>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <span className="text-muted" style={{ fontSize: 12 }}>FECHA EMISIÓN</span>
        <span>{formatFecha(factura.fecha_emision)}</span>
      </div>
      <div className="d-flex justify-content-between align-items-start border-top pt-2 mt-1">
        <span className="text-muted fw-semibold" style={{ fontSize: 12 }}>TOTAL</span>
        <span className="fw-bold text-danger fs-6">${formatMonto(factura.total)}</span>
      </div>
    </div>
  );
}

function ConfirmarNctModal({ factura, onConfirmar, onCancelar, procesando }) {
  if (!factura) return null;
  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content border-0 shadow">
          <div className="modal-header border-bottom pb-3">
            <div className="d-flex align-items-center gap-2">
              <div className="bg-danger bg-opacity-10 rounded-circle p-2">
                <i className="bi bi-file-earmark-minus text-danger fs-5" />
              </div>
              <div>
                <h6 className="modal-title fw-bold mb-0">Generar nota de crédito (NCT)</h6>
                <small className="text-muted">Esta acción no se puede deshacer</small>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onCancelar} disabled={procesando} />
          </div>
          <div className="modal-body py-3">
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
              Se emitirá una <strong>nota de crédito (NCT)</strong> en Contífico que revierte la siguiente factura. Úsala cuando la factura ya fue autorizada por el SRI.
            </p>
            <ResumenFacturaBox factura={factura} />
            <div className="alert alert-warning py-2 mb-0" style={{ fontSize: 12 }}>
              <i className="bi bi-exclamation-triangle-fill me-2" />
              La NCT quedará registrada en Contífico y el estado de la factura cambiará a <strong>Anulado</strong>.
            </div>
          </div>
          <div className="modal-footer border-top pt-3">
            <button type="button" className="btn btn-outline-secondary" onClick={onCancelar} disabled={procesando}>
              Cancelar
            </button>
            <button type="button" className="btn btn-danger" onClick={onConfirmar} disabled={procesando}>
              {procesando
                ? <><span className="spinner-border spinner-border-sm me-2" />Emitiendo NCT...</>
                : <><i className="bi bi-file-earmark-minus me-2" />Generar nota de crédito</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmarAnularDirectoModal({ factura, onConfirmar, onCancelar, procesando }) {
  if (!factura) return null;
  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content border-0 shadow">
          <div className="modal-header border-bottom pb-3">
            <div className="d-flex align-items-center gap-2">
              <div className="bg-warning bg-opacity-10 rounded-circle p-2">
                <i className="bi bi-slash-circle text-warning fs-5" />
              </div>
              <div>
                <h6 className="modal-title fw-bold mb-0">Anular factura</h6>
                <small className="text-muted">Cambia el estado a "Anulado" en Contífico</small>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onCancelar} disabled={procesando} />
          </div>
          <div className="modal-body py-3">
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
              Se marcará la siguiente factura como <strong>Anulada (A)</strong> directamente en Contífico. Úsala cuando la factura <strong>no fue enviada ni autorizada por el SRI</strong>.
            </p>
            <ResumenFacturaBox factura={factura} />
            <div className="alert alert-danger py-2 mb-0" style={{ fontSize: 12 }}>
              <i className="bi bi-exclamation-octagon-fill me-2" />
              <strong>Importante:</strong> Si esta factura ya fue autorizada por el SRI, debes anularla también manualmente en el <strong>portal del SRI</strong>. Esta acción solo actualiza el estado en Contífico.
            </div>
          </div>
          <div className="modal-footer border-top pt-3">
            <button type="button" className="btn btn-outline-secondary" onClick={onCancelar} disabled={procesando}>
              Cancelar
            </button>
            <button type="button" className="btn btn-warning text-dark" onClick={onConfirmar} disabled={procesando}>
              {procesando
                ? <><span className="spinner-border spinner-border-sm me-2" />Anulando...</>
                : <><i className="bi bi-slash-circle me-2" />Confirmar anulación</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetallePanel({ factura, onCerrar, onAnulada }) {
  const [procesandoNct,     setProcesandoNct]     = useState(false);
  const [procesandoDirecto, setProcesandoDirecto] = useState(false);
  const [confirmarNct,      setConfirmarNct]      = useState(false);
  const [confirmarDirecto,  setConfirmarDirecto]  = useState(false);

  if (!factura) return null;
  const persona     = factura.cliente || {};
  const esFAC       = factura._tipo !== 'NCT';
  const puedeActuar = esFAC && factura.estado !== 'A' && !factura.anulado;

  const handleNct = async () => {
    setProcesandoNct(true);
    setConfirmarNct(false);
    try {
      await anularFactura(factura.id, `Nota de crédito: ${factura.documento || factura.id}`);
      showToast('success', 'Nota de crédito (NCT) emitida correctamente.');
      onAnulada?.();
      onCerrar();
    } catch (err) {
      showToast('error', err?.message || 'No se pudo generar la nota de crédito.');
    } finally {
      setProcesandoNct(false);
    }
  };

  const handleAnularDirecto = async () => {
    setProcesandoDirecto(true);
    setConfirmarDirecto(false);
    try {
      await anularDocumentoDirecto(factura.id);
      showToast('success', 'Factura marcada como anulada en Contífico.');
      onAnulada?.();
      onCerrar();
    } catch (err) {
      showToast('error', err?.message || 'No se pudo anular la factura.');
    } finally {
      setProcesandoDirecto(false);
    }
  };

  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-header bg-white border-bottom d-flex align-items-center justify-content-between py-3">
        <div>
          <h6 className="mb-0 fw-bold">{factura.documento || '—'}</h6>
          <small className="text-muted">{formatFecha(factura.fecha_emision)}</small>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={onCerrar}>
          <i className="bi bi-x-lg" />
        </button>
      </div>
      <div className="card-body overflow-auto" style={{ fontSize: 13 }}>

        {/* Estado */}
        <div className="d-flex gap-2 mb-3 flex-wrap">
          <BadgeEstado estado={factura.estado} />
          <BadgeSRI autorizado={factura.autorizado_sri} enviado={factura.enviado_sri} />
          {factura.anulado && <span className="badge bg-danger">Anulado</span>}
        </div>

        {/* Cliente */}
        <p className="text-muted mb-1 fw-semibold" style={{ fontSize: 11 }}>CLIENTE</p>
        <p className="mb-0 fw-semibold">{persona.razon_social || '—'}</p>
        <p className="mb-3 text-muted">{persona.cedula || persona.ruc || '—'} · {persona.email || '—'}</p>

        {/* Montos */}
        <p className="text-muted mb-1 fw-semibold" style={{ fontSize: 11 }}>MONTOS</p>
        <table className="table table-sm table-borderless mb-3">
          <tbody>
            <tr><td className="text-muted">Subtotal 0%</td><td className="text-end">${formatMonto(factura.subtotal_0)}</td></tr>
            <tr><td className="text-muted">Subtotal IVA</td><td className="text-end">${formatMonto(factura.subtotal_12 || factura.subtotal)}</td></tr>
            <tr><td className="text-muted">IVA</td><td className="text-end">${formatMonto(factura.iva)}</td></tr>
            <tr className="fw-bold border-top"><td>Total</td><td className="text-end">${formatMonto(factura.total)}</td></tr>
            <tr><td className="text-muted">Saldo</td><td className="text-end">${formatMonto(factura.saldo)}</td></tr>
          </tbody>
        </table>

        {/* Descripción */}
        {factura.descripcion && (
          <>
            <p className="text-muted mb-1 fw-semibold" style={{ fontSize: 11 }}>DESCRIPCIÓN</p>
            <p className="mb-3">{factura.descripcion}</p>
          </>
        )}

        {/* Autorización SRI */}
        {factura.autorizacion && (
          <>
            <p className="text-muted mb-1 fw-semibold" style={{ fontSize: 11 }}>AUTORIZACIÓN SRI</p>
            <p className="mb-3" style={{ wordBreak: 'break-all', fontSize: 12 }}>{factura.autorizacion}</p>
          </>
        )}

        {/* Productos */}
        {Array.isArray(factura.detalles) && factura.detalles.length > 0 && (
          <>
            <p className="text-muted mb-1 fw-semibold" style={{ fontSize: 11 }}>PRODUCTOS</p>
            <table className="table table-sm mb-3">
              <thead><tr><th>Producto</th><th className="text-end">Cant.</th><th className="text-end">Precio</th><th className="text-end">Total</th></tr></thead>
              <tbody>
                {factura.detalles.map((d, i) => (
                  <tr key={i}>
                    <td>{d.producto_nombre || d.descripcion || '—'}</td>
                    <td className="text-end">{Number(d.cantidad || 0)}</td>
                    <td className="text-end">${formatMonto(d.precio)}</td>
                    <td className="text-end">${formatMonto(Number(d.cantidad || 0) * Number(d.precio || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Links RIDE / XML */}
        {(factura.url_ride || factura.url_xml) && (
          <div className="d-flex gap-2 flex-wrap mb-3">
            {factura.url_ride && (
              <a href={factura.url_ride} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                <i className="bi bi-file-earmark-pdf me-1" />RIDE
              </a>
            )}
            {factura.url_xml && (
              <a href={factura.url_xml} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary">
                <i className="bi bi-file-earmark-code me-1" />XML
              </a>
            )}
          </div>
        )}

        {/* Acciones de anulación */}
        {puedeActuar && (
          <div className="border-top pt-3 mt-2 d-flex flex-column gap-2">
            <button
              className="btn btn-sm btn-outline-warning w-100"
              onClick={() => setConfirmarDirecto(true)}
              disabled={procesandoDirecto || procesandoNct}
            >
              {procesandoDirecto
                ? <><span className="spinner-border spinner-border-sm me-2" />Anulando...</>
                : <><i className="bi bi-slash-circle me-2" />Anular factura</>}
            </button>
            <button
              className="btn btn-sm btn-outline-danger w-100"
              onClick={() => setConfirmarNct(true)}
              disabled={procesandoNct || procesandoDirecto}
            >
              {procesandoNct
                ? <><span className="spinner-border spinner-border-sm me-2" />Emitiendo NCT...</>
                : <><i className="bi bi-file-earmark-minus me-2" />Generar nota de crédito</>}
            </button>
          </div>
        )}
      </div>

      {confirmarNct && (
        <ConfirmarNctModal
          factura={factura}
          onConfirmar={handleNct}
          onCancelar={() => setConfirmarNct(false)}
          procesando={procesandoNct}
        />
      )}
      {confirmarDirecto && (
        <ConfirmarAnularDirectoModal
          factura={factura}
          onConfirmar={handleAnularDirecto}
          onCancelar={() => setConfirmarDirecto(false)}
          procesando={procesandoDirecto}
        />
      )}
    </div>
  );
}

// ── Badge estado SRI (facturas locales) ──────────────────────────────────────

const ESTADOS_SRI = {
  no_enviado: { label: 'Pendiente',    color: 'warning',   icon: 'bi-hourglass' },
  enviado:    { label: 'En SRI',       color: 'info',      icon: 'bi-send' },
  autorizado: { label: 'Autorizado',   color: 'success',   icon: 'bi-check-circle-fill' },
  rechazado:  { label: 'Rechazado',    color: 'danger',    icon: 'bi-x-circle-fill' },
};

function BadgeEstadoSRI({ estado }) {
  const e = ESTADOS_SRI[estado] || { label: estado || '—', color: 'secondary', icon: 'bi-dash' };
  return (
    <span className={`badge bg-${e.color} ${e.color === 'warning' ? 'text-dark' : ''}`}>
      <i className={`bi ${e.icon} me-1`} />{e.label}
    </span>
  );
}

// ── Tab: facturas locales (Ademy) ─────────────────────────────────────────────

function FacturasLocalesTab() {
  const [docs,       setDocs]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [reintentando, setReintentando] = useState(null); // id de la factura en proceso
  const [filtros, setFiltros] = useState({
    fecha_inicial: primerDiaMes(),
    fecha_final:   hoy(),
    estado_sri:    '',
    page:          1,
    page_size:     50,
  });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarFacturasLocales(filtros);
      setDocs(data.results || []);
      setTotal(data.total || 0);
    } catch (err) {
      showToast('error', err?.message || 'Error al cargar facturas locales.');
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleReintentar = async (factura) => {
    setReintentando(factura.id);
    try {
      await reintentarFacturaLocal(factura.id);
      const nombre = [factura.cliente_apellidos, factura.cliente_nombres].filter(Boolean).join(', ') || 'el estudiante';
      showToast({ type: 'success', message: `Factura emitida a Contífico para ${nombre}.` });
      cargar();
    } catch (err) {
      showToast({ type: 'error', message: err?.message || 'No se pudo reintentar la factura.' });
    } finally {
      setReintentando(null);
    }
  };

  const totalImporte  = docs.reduce((s, f) => s + Number(f.total || 0), 0);
  const totalAuth     = docs.filter(f => f.estado_sri === 'autorizado').length;
  const totalPending  = docs.filter(f => ['no_enviado', 'enviado'].includes(f.estado_sri)).length;

  return (
    <div>
      {/* Resumen */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Total',      value: total,        icon: 'bi-receipt',       color: 'primary', isMoney: false },
          { label: 'Importe',    value: totalImporte,  icon: 'bi-cash-stack',    color: 'info',    isMoney: true  },
          { label: 'Autorizadas', value: totalAuth,   icon: 'bi-check-circle',  color: 'success', isMoney: false },
          { label: 'Pendientes', value: totalPending,  icon: 'bi-hourglass',     color: 'warning', isMoney: false },
        ].map(({ label, value, icon, color, isMoney }) => (
          <div className="col-6 col-md-3" key={label}>
            <div className="card border-0 shadow-sm">
              <div className="card-body py-3">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <i className={`bi ${icon} text-${color}`} />
                  <small className="text-muted">{label}</small>
                </div>
                <p className="mb-0 fw-bold fs-5">
                  {isMoney ? `$${formatMonto(value)}` : value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-auto">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Desde</label>
              <input type="date" className="form-control form-control-sm"
                value={filtros.fecha_inicial}
                onChange={e => setFiltros(p => ({ ...p, fecha_inicial: e.target.value, page: 1 }))} />
            </div>
            <div className="col-auto">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Hasta</label>
              <input type="date" className="form-control form-control-sm"
                value={filtros.fecha_final}
                onChange={e => setFiltros(p => ({ ...p, fecha_final: e.target.value, page: 1 }))} />
            </div>
            <div className="col-auto">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Estado SRI</label>
              <select className="form-select form-select-sm" value={filtros.estado_sri}
                onChange={e => setFiltros(p => ({ ...p, estado_sri: e.target.value, page: 1 }))}
                style={{ minWidth: 150 }}>
                <option value="">Todos</option>
                {Object.entries(ESTADOS_SRI).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary btn-sm" onClick={cargar} disabled={loading}>
                <i className="bi bi-search me-1" />Buscar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="card border-0 shadow-sm">
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <p className="text-muted mt-2 mb-0">Cargando facturas...</p>
          </div>
        )}
        {!loading && docs.length === 0 && (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-receipt fs-1 d-block mb-2 opacity-25" />
            No hay facturas locales para el período seleccionado.
          </div>
        )}
        {!loading && docs.length > 0 && (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{ fontSize: 13 }}>
              <thead className="table-light">
                <tr>
                  <th>N° FAC</th>
                  <th>Fecha</th>
                  <th>Estudiante</th>
                  <th>Curso</th>
                  <th className="text-end">Total</th>
                  <th>Estado SRI</th>
                  <th>Intentos</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {docs.map(f => (
                  <tr key={f.id}>
                    <td className="fw-semibold">
                      {f.numero_factura
                        ? <span className="text-success">{f.numero_factura}</span>
                        : <span className="text-muted fst-italic">Pendiente</span>}
                    </td>
                    <td>{f.created_at ? new Date(f.created_at).toLocaleDateString('es-EC') : '—'}</td>
                    <td>
                      <div>{[f.cliente_apellidos, f.cliente_nombres].filter(Boolean).join(', ') || '—'}</div>
                      <small className="text-muted">{f.cliente_cedula || ''}</small>
                    </td>
                    <td>{f.curso_nombre || '—'}</td>
                    <td className="text-end fw-semibold">${formatMonto(f.total)}</td>
                    <td><BadgeEstadoSRI estado={f.estado_sri} /></td>
                    <td className="text-center">
                      {f.estado_sri === 'rechazado'
                        ? <span className="badge bg-danger">{f.intentos_reintento}</span>
                        : <span className="text-muted">{f.intentos_reintento}</span>}
                    </td>
                    <td>
                      {f.estado_sri === 'rechazado' && (
                        <button
                          className="btn btn-sm btn-outline-warning"
                          title="Reintentar emisión a Contífico"
                          onClick={() => handleReintentar(f)}
                          disabled={reintentando === f.id}
                        >
                          {reintentando === f.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : <i className="bi bi-arrow-clockwise" />}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && total > filtros.page_size && (
          <div className="card-footer bg-transparent d-flex justify-content-between align-items-center py-2" style={{ fontSize: 13 }}>
            <span className="text-muted">Mostrando {docs.length} de {total}</span>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" disabled={filtros.page <= 1}
                onClick={() => setFiltros(p => ({ ...p, page: p.page - 1 }))}>
                <i className="bi bi-chevron-left" />
              </button>
              <span className="align-self-center">Pág. {filtros.page}</span>
              <button className="btn btn-outline-secondary btn-sm"
                disabled={filtros.page * filtros.page_size >= total}
                onClick={() => setFiltros(p => ({ ...p, page: p.page + 1 }))}>
                <i className="bi bi-chevron-right" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Vista principal ───────────────────────────────────────────────────────────

export default function FacturasEmitidasView() {
  const [tab, setTab]               = useState('contifico');
  const [docs, setDocs]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [seleccionada, setSeleccionada] = useState(null);

  const [filtros, setFiltros] = useState({
    tipo:          'all', // 'all' | 'fac' | 'nct'
    fecha_inicial: primerDiaMes(),
    fecha_final:   hoy(),
    estado:        '',
    page:          1,
    page_size:     50,
  });

  const cargar = useCallback(async () => {
    setLoading(true);
    setSeleccionada(null);
    try {
      if (filtros.tipo === 'all') {
        const [dataFac, dataNct] = await Promise.all([
          listarFacturas(filtros),
          listarNotasCredito(filtros),
        ]);
        const lista = [
          ...(dataFac.results || []).map(d => ({ ...d, _tipo: 'FAC' })),
          ...(dataNct.results || []).map(d => ({ ...d, _tipo: 'NCT' })),
        ].sort((a, b) => {
          // Ordenar por fecha desc (formato dd/mm/yyyy)
          const toTs = f => {
            if (!f) return 0;
            const [d, m, y] = f.split('/');
            return new Date(`${y}-${m}-${d}`).getTime();
          };
          return toTs(b.fecha_emision) - toTs(a.fecha_emision);
        });
        setDocs(lista);
        setTotal((dataFac.total || 0) + (dataNct.total || 0));
      } else if (filtros.tipo === 'fac') {
        const data = await listarFacturas(filtros);
        setDocs((data.results || []).map(d => ({ ...d, _tipo: 'FAC' })));
        setTotal(data.total || 0);
      } else {
        const data = await listarNotasCredito(filtros);
        setDocs((data.results || []).map(d => ({ ...d, _tipo: 'NCT' })));
        setTotal(data.total || 0);
      }
    } catch (err) {
      showToast('error', err?.message || 'Error al cargar documentos de Contífico.');
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  const totalImporte = docs.reduce((s, f) => s + Number(f.total || 0), 0);
  const totalCobrado = docs.filter(f => ['C','G'].includes(f.estado)).reduce((s, f) => s + Number(f.total || 0), 0);
  const totalAnulado = docs.filter(f => f.estado === 'A').reduce((s, f) => s + Number(f.total || 0), 0);

  return (
    <div className="container-fluid py-3">

      {/* Encabezado */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <div className="bg-primary bg-opacity-10 rounded-3 p-3">
          <i className="bi bi-receipt fs-3 text-primary" />
        </div>
        <div>
          <h4 className="mb-0 fw-bold">Facturación electrónica</h4>
          <small className="text-muted">Documentos emitidos — Contífico</small>
        </div>
        <button className="btn btn-outline-primary btn-sm ms-auto" onClick={cargar} disabled={loading}>
          <i className={`bi bi-arrow-clockwise me-1 ${loading ? 'spin' : ''}`} />Actualizar
        </button>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${tab === 'contifico' ? 'active' : ''}`}
            onClick={() => setTab('contifico')}
          >
            <i className="bi bi-cloud me-2" />Todas en Contífico
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${tab === 'local' ? 'active' : ''}`}
            onClick={() => setTab('local')}
          >
            <i className="bi bi-hdd me-2" />Emitidas desde Ademy
          </button>
        </li>
      </ul>

      {tab === 'local' && <FacturasLocalesTab />}

      {tab === 'contifico' && <>

      {/* Resumen */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Documentos', value: total,        icon: 'bi-receipt',      color: 'primary', isMoney: false },
          { label: 'Importe',    value: totalImporte,  icon: 'bi-cash-stack',   color: 'info',    isMoney: true  },
          { label: 'Cobrado',    value: totalCobrado,  icon: 'bi-check-circle', color: 'success', isMoney: true  },
          { label: 'Anulado',    value: totalAnulado,  icon: 'bi-x-circle',     color: 'danger',  isMoney: true  },
        ].map(({ label, value, icon, color, isMoney }) => (
          <div className="col-6 col-md-3" key={label}>
            <div className="card border-0 shadow-sm">
              <div className="card-body py-3">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <i className={`bi ${icon} text-${color}`} />
                  <small className="text-muted">{label}</small>
                </div>
                <p className="mb-0 fw-bold fs-5">
                  {isMoney ? `$${formatMonto(value)}` : value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-auto">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Tipo</label>
              <select
                className="form-select form-select-sm"
                value={filtros.tipo}
                onChange={e => setFiltros(p => ({ ...p, tipo: e.target.value, page: 1 }))}
                style={{ minWidth: 160 }}
              >
                <option value="all">Todos</option>
                <option value="fac">Facturas (FAC)</option>
                <option value="nct">Notas de crédito (NCT)</option>
              </select>
            </div>
            <div className="col-auto">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Desde</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filtros.fecha_inicial}
                onChange={e => setFiltros(p => ({ ...p, fecha_inicial: e.target.value, page: 1 }))}
              />
            </div>
            <div className="col-auto">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Hasta</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filtros.fecha_final}
                onChange={e => setFiltros(p => ({ ...p, fecha_final: e.target.value, page: 1 }))}
              />
            </div>
            <div className="col-auto">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Estado</label>
              <select
                className="form-select form-select-sm"
                value={filtros.estado}
                onChange={e => setFiltros(p => ({ ...p, estado: e.target.value, page: 1 }))}
                style={{ minWidth: 140 }}
              >
                <option value="">Todos</option>
                {Object.entries(ESTADOS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary btn-sm" onClick={cargar} disabled={loading}>
                <i className="bi bi-search me-1" />Buscar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla + Panel */}
      <div className="row g-3">
        <div className={seleccionada ? 'col-md-7' : 'col-12'}>
          <div className="card border-0 shadow-sm">
            {loading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
                <p className="text-muted mt-2 mb-0">Consultando Contífico...</p>
              </div>
            )}
            {!loading && docs.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-receipt fs-1 d-block mb-2 opacity-25" />
                No hay documentos para el período seleccionado.
              </div>
            )}
            {!loading && docs.length > 0 && (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0" style={{ fontSize: 13 }}>
                  <thead className="table-light">
                    <tr>
                      <th>N° Documento</th>
                      <th>Tipo</th>
                      <th>Fecha</th>
                      <th>Cliente</th>
                      <th className="text-end">Total</th>
                      <th>Estado</th>
                      <th>SRI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map(f => (
                      <tr
                        key={`${f._tipo}-${f.id}`}
                        onClick={() => setSeleccionada(sel => sel?.id === f.id ? null : f)}
                        style={{ cursor: 'pointer' }}
                        className={seleccionada?.id === f.id ? 'table-active' : ''}
                      >
                        <td className="fw-semibold">{f.documento || '—'}</td>
                        <td>
                          {f._tipo === 'NCT'
                            ? <span className="badge bg-warning text-dark">NCT</span>
                            : <span className="badge bg-primary">FAC</span>}
                        </td>
                        <td>{formatFecha(f.fecha_emision)}</td>
                        <td>
                          <div>{f.cliente?.razon_social || '—'}</div>
                          <small className="text-muted">{f.cliente?.cedula || f.cliente?.ruc || ''}</small>
                        </td>
                        <td className="text-end fw-semibold">${formatMonto(f.total)}</td>
                        <td><BadgeEstado estado={f.estado} /></td>
                        <td><BadgeSRI autorizado={f.autorizado_sri} enviado={f.enviado_sri} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {seleccionada && (
          <div className="col-md-5">
            <DetallePanel
              factura={seleccionada}
              onCerrar={() => setSeleccionada(null)}
              onAnulada={() => { setSeleccionada(null); cargar(); }}
            />
          </div>
        )}
      </div>

      </>}

    </div>
  );
}
